import { Box, Typography, Alert } from "@mui/material";
import { DataGrid, GridColDef, GridToolbar } from "@mui/x-data-grid";
import { useState, useEffect } from "react";

interface ModelRecord {
  id: string;
  vendor: string;
  host: string;
  name: string;
  description?: string | null;
  modelKey?: string | null;
  inputTokenPrice?: number | null;
  outputTokenPrice?: number | null;
  textInputSupport: boolean;
  imageInputSupport: boolean;
  audioInputSupport: boolean;
  textOutputSupport: boolean;
  imageOutputSupport: boolean;
  audioOutputSupport: boolean;
  thinkingSupport: boolean;
  contextWindowTokens?: number | null;
  maxResponseTokens?: number | null;
  knowledgeCutoffDate: string | null;
  lastCheckedDate: string | null;
  supportedLanguages?: string | null;
}

export default function ModelsPublic() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [models, setModels] = useState<ModelRecord[]>([]);

  useEffect(() => {
    fetchModels();
  }, []);

  const fetchModels = async () => {
    setLoading(true);
    let url = "https://api.llmasaservice.io/models";

    if (
      window.location.hostname === "localhost" ||
      window.location.hostname === "dev.llmasaservice.io"
    ) {
      url = "https://8ftw8droff.execute-api.us-east-1.amazonaws.com/dev/models";
    }

    try {
      const result = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!result.ok) {
        setError("Error fetching models: " + result.statusText);
        return;
      }
      const data = await result.json();

      setModels(data);
    } catch (err) {
      setError("Error fetching models: " + String(err));
    }

    setLoading(false);
  };

  const columns: GridColDef[] = [
    {
      field: "vendor",
      headerName: "Vendor / Host",
      width: 130,
      valueGetter: (value, row) => {
        if (row.host !== value) {
          return `${value || ""} / ${row.host || ""}`;
        }
        return value || "";
      },
    },
    { field: "name", headerName: "Model Name", width: 200 },
    { field: "description", headerName: "Description", width: 400 },
    {
      field: "inputTokenPrice",
      headerName: "Input Price",
      width: 100,
      type: "number",
      valueFormatter: (value?: number) => {
        if (value == null) {
          return "-";
        }
        return `$${Number(value).toFixed(3)}`;
      },
    },
    {
      field: "outputTokenPrice",
      headerName: "Output Price",
      width: 100,
      type: "number",
      valueFormatter: (value?: number) => {
        if (value == null) {
          return "-";
        }
        return `$${Number(value).toFixed(3)}`;
      },
    },
    {
      field: "contextWindowTokens",
      headerName: "Max Context",
      width: 100,
      type: "number",
    },
    {
      field: "maxResponseTokens",
      headerName: "Max Response",
      width: 100,
      type: "number",
    },
    {
      field: "thinkingSupport",
      headerName: "Thinking",
      width: 80,
      type: "boolean",
    },
    {
      field: "textInputSupport",
      headerName: "Text In",
      width: 80,
      type: "boolean",
    },
    {
      field: "imageInputSupport",
      headerName: "Image In",
      width: 80,
      type: "boolean",
    },
    {
      field: "audioInputSupport",
      headerName: "Audio In",
      width: 80,
      type: "boolean",
    },
    {
      field: "textOutputSupport",
      headerName: "Text Out",
      width: 80,
      type: "boolean",
    },
    {
      field: "imageOutputSupport",
      headerName: "Image Out",
      width: 90,
      type: "boolean",
    },
    {
      field: "audioOutputSupport",
      headerName: "Audio Out",
      width: 90,
      type: "boolean",
    },
    {
      field: "knowledgeCutoffDate",
      headerName: "Training Cutoff",
      width: 120,
      type: "string",
      valueFormatter: (value?: string) => {
        if (value == null) {
          return "-";
        }
        return new Date(value)
          .toLocaleDateString("en-US", {
            year: "numeric",
            month: "short",
          })
          .replace(",", "");
      },
    },
    {
      field: "lastCheckedDate",
      headerName: "Last Checked",
      width: 120,
      type: "string",
      valueFormatter: (value?: string) => {
        if (value == null) {
          return "-";
        }
        return new Date(value)
          .toLocaleDateString("en-US", {
            year: "numeric",
            month: "short",
          })
          .replace(",", "");
      },
    },
    {
      field: "supportedLanguages",
      headerName: "Supported Languages",
      width: 200,
    },
  ];

  return (
    <Box p={1}>
      <Typography variant="h5" gutterBottom>
        Model Explorer
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box
        sx={{
          width: "100%",
        }}
      >
        <DataGrid
          rows={models}
          columns={columns}
          getRowHeight={() => "auto"}
          disableColumnResize
          initialState={{
            pagination: {
              paginationModel: { pageSize: 10, page: 0 },
            },
            sorting: {
              sortModel: [{ field: "outputTokenPrice", sort: "desc" }],
            },
            columns: {
              columnVisibilityModel: {
                id: false,
                vendor: true,
                //host: false,
                //supported: false,
                //status: false,
                name: true,
                description: false,
                inputTokenPrice: true,
                outputTokenPrice: true,
                contextWindowTokens: true,
                maxResponseTokens: true,
                textInputSupport: true,
                imageInputSupport: true,
                audioInputSupport: true,
                textOutputSupport: true,
                imageOutputSupport: true,
                audioOutputSupport: true,
                thinkingSupport: true,
                knowledgeCutoffDate: true,
                lastCheckedDate: false,
                supportedLanguages: false,
              },
            },
          }}
          pageSizeOptions={[10, 20, 30, { value: -1, label: "All" }]}
          loading={loading}
          sx={{
            "& .MuiDataGrid-cell": {
              py: 1,
            },
          }}
          slots={{ toolbar: GridToolbar }}
          slotProps={{
            toolbar: {
              showQuickFilter: true,
            },
          }}
        />
      </Box>

      <Typography variant="caption" sx={{ mt: 2, color: "text.secondary" }}>
        TAKE CARE: This list is not exhaustive and does not include all
        available models or versions of models. We strive for timely & accurate
        information, but this data will have errors. Pricing changes often, so
        please check with the vendor for the most accurate information. Pricing
        hosted on Amazon is based on US-EAST-1 region as a baseline. If you have
        suggestion or want to report an error:{" "}
        <a href="mailto:support@llmasaservice.io">Support</a>.
      </Typography>
      <Typography variant="caption" sx={{ mt: 1, color: "text.secondary" }}>
        &nbsp;&nbsp;Sources:{" "}
        <Box sx={{ display: "inline-flex", gap: 2 }}>
          <a
            href="https://openai.com/api/pricing/"
            target="_blank"
            rel="noopener noreferrer"
          >
            OpenAI
          </a>
          <a
            href="https://docs.anthropic.com/en/docs/about-claude/models/all-models"
            target="_blank"
            rel="noopener noreferrer"
          >
            Anthropic
          </a>
          <a
            href="https://docs.mistral.ai/getting-started/models/models_overview/"
            target="_blank"
            rel="noopener noreferrer"
          >
            Mistral
          </a>
          <a
            href="https://ai.google.dev/gemini-api/docs/models"
            target="_blank"
            rel="noopener noreferrer"
          >
            Google
          </a>
          <a
            href="https://aws.amazon.com/bedrock/pricing/"
            target="_blank"
            rel="noopener noreferrer"
          >
            Amazon
          </a>
          <a
            href="https://groq.com/pricing/"
            target="_blank"
            rel="noopener noreferrer"
          >
            Groq
          </a>
          <a
            href="https://api-docs.deepseek.com/quick_start/pricing"
            target="_blank"
            rel="noopener noreferrer"
          >
            Deepseek
          </a>
        </Box>
      </Typography>
    </Box>
  );
}
